import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import type { Licitacion } from "@/types/licitacion";
import {
  Calendar,
  DollarSign,
  Building2,
  Tag,
  FileText,
  Link as LinkIcon,
  Edit,
  Copy,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PrioridadBadge } from "@/components/licitaciones/estado-badge";
import { CloneLicitacionDialog } from "@/components/licitaciones/clone-licitacion-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useAuth } from "@/components/providers/auth-provider";
import { useDeleteLicitacion } from "@/hooks/use-licitaciones";

function formatCurrency(value: number | null): string {
  if (value === null) return "\u2014";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
}

export default function LicitacionResumenPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteLicitacion();

  const { data: licitacion } = useQuery<Licitacion>({
    queryKey: ["licitacion", id],
    queryFn: () => fetchWithAuth(`/api/v1/licitaciones/${id}`),
    enabled: !!id,
  });

  if (!licitacion) return null;

  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(id);
      setDeleteDialogOpen(false);
      navigate("/licitaciones", { replace: true });
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "No se pudo eliminar la licitación"
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex justify-end gap-2">
        {/* Mejora H (changelog cliente): clonar como plantilla. */}
        <button
          type="button"
          onClick={() => setCloneDialogOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="Crear una nueva licitación copiando esta como plantilla"
        >
          <Copy className="w-4 h-4" />
          Usar como plantilla
        </button>
        <Link
          to={`/licitaciones/${id}/editar`}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit className="w-4 h-4" />
          Editar
        </Link>
        {/* Solo admins ven el botón de eliminar. El backend además
            valida is_admin y devuelve 403 si alguien sin admin llama
            al endpoint directamente. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setDeleteError(null);
              setDeleteDialogOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
            title="Eliminar esta licitación de forma permanente"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
        )}
      </div>

      {/* Info grid */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Información General</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Organismo</p>
              <p className="text-sm font-medium text-gray-900">{licitacion.organismo}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Referencia</p>
              <p className="text-sm font-medium text-gray-900">{licitacion.referencia || "\u2014"}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Presupuesto base</p>
              <p className="text-sm font-medium text-gray-900">{formatCurrency(licitacion.presupuesto_base)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Valor estimado</p>
              <p className="text-sm font-medium text-gray-900">{formatCurrency(licitacion.valor_estimado)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Fecha publicación</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(licitacion.fecha_publicacion)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Fecha límite</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(licitacion.fecha_limite)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Prioridad</p>
              <PrioridadBadge prioridad={licitacion.prioridad} />
            </div>
          </div>

          {licitacion.url_plataforma && (
            <div className="flex items-start gap-3">
              <LinkIcon className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Plataforma</p>
                <a href={licitacion.url_plataforma} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                  Ver en plataforma
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {licitacion.descripcion && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Descripción</h2>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{licitacion.descripcion}</p>
        </div>
      )}

      {/* Tags */}
      {(licitacion.etiquetas ?? []).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Etiquetas</h2>
          <div className="flex flex-wrap gap-2">
            {(licitacion.etiquetas ?? []).map((tag) => (
              <span key={tag} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {licitacion.notas && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Notas internas</h2>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{licitacion.notas}</p>
        </div>
      )}

      {/* Mejora H (changelog cliente): diálogo de clonación. */}
      <CloneLicitacionDialog
        isOpen={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        sourceId={licitacion.id}
        sourceTitle={licitacion.titulo}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleteMutation.isPending) setDeleteDialogOpen(open);
        }}
        title="Eliminar licitación"
        description={
          deleteError
            ? `Error al eliminar: ${deleteError}`
            : `¿Seguro que quieres eliminar “${licitacion.titulo}”? Esta acción es permanente y borrará también sus documentos, propuestas y conversaciones asociadas.`
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
